import React from 'react';
import { TrendingUp, Banknote, Building2, Wrench, Shield, CheckCircle } from 'lucide-react';
import { SectionHeader } from '../../primitives/SectionHeader';
import { BenefitCard } from '../../cards/BenefitCard';
import { colors, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';
import { InlineEditableText } from '../../../InlineEditableText';

const ITEMS = [
  { icon: TrendingUp, accent: colors.orange, title: 'Jahrelange Marktexpertise', desc: 'Mehr als 10 Jahre Erfahrung in der Planung, Entwicklung und Realisierung gewünschter Photovoltaik-Projekte – von Kleinanlagen bis Großprojekten im Megawattbereich.' },
  { icon: Banknote, accent: colors.navy, title: 'Garantierte Pachteinnahmen', desc: 'Sie erhalten jährliche Pachtzahlungen – garantiert und unabhängig von Sonneneinstrahlung, Wetterbedingungen oder Marktpreisschwankungen.' },
  { icon: Building2, accent: colors.orange, title: 'Nachhaltige Immobilienaufwertung', desc: 'Eine modern ausgerüstete Photovoltaikanlage erhöht den Wert Ihrer Gewerbeimmobilie und signalisiert Nachhaltigkeitsengagement.' },
  { icon: Wrench, accent: colors.navy, title: 'Kostenfreie Dachsanierung', desc: 'Im Rahmen der PV-Installation übernimmt EN:POWER alle nötigen Dacharbeiten: Abdichtung, Substanzsicherung und Montagevorbereitungen.' },
  { icon: Shield, accent: colors.orange, title: 'Null Risiko für Sie', desc: 'EN:POWER übernimmt die vollständige technische Verantwortung: Betriebsversicherung, Wartung, Monitoring und Störungsbeseitigung.' },
  { icon: CheckCircle, accent: colors.navy, title: 'Komplett kostenfrei', desc: 'Von der ersten Dachanalyse über die Installation bis zum laufenden Betrieb fallen für Sie keine Kosten an. Keine versteckten Gebühren.' },
];

/**
 * BenefitsSection – "Ihre Vorteile auf einen Blick" auf Page 2.
 * Jede der 6 BenefitCards wird aus genau derselben Komponente erzeugt.
 */
export const BenefitsSection = ({ content = {}, onTextChange, editMode = false, layoutEditLevel = 'section', dragProps = {} }) => {
  const benefitsText = {
    header: {
      eyebrow: content?.header?.eyebrow || 'Was Sie gewinnen',
      headline: content?.header?.headline || 'IHRE VORTEILE AUF EINEN BLICK',
    },
    items: (content?.items && Array.isArray(content.items) && content.items.length === ITEMS.length
      ? content.items
      : ITEMS.map((item) => ({ title: item.title, desc: item.desc }))),
  };

  return (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, background: 'white', minHeight: 0 }}>
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S2-Vorteile-Header" {...dragProps} dragAxis="y">
        <div style={{ padding: `${space.lg}px ${space.xxl}px ${space.sm}px`, background: 'white', flexShrink: 0 }}>
          <SectionHeader
            eyebrow={(
              <InlineEditableText
                as="span"
                value={benefitsText.header.eyebrow}
                editable={editMode}
                onChange={(value) => onTextChange?.('page2.benefits.header.eyebrow', value)}
              />
            )}
            headline={(
              <InlineEditableText
                as="span"
                value={benefitsText.header.headline}
                editable={editMode}
                onChange={(value) => onTextChange?.('page2.benefits.header.headline', value)}
              />
            )}
          />
        </div>
      </DraggableElement>
    ) : (
      <div style={{ padding: `${space.lg}px ${space.xxl}px ${space.sm}px`, background: 'white', flexShrink: 0 }}>
        <SectionHeader
          eyebrow={(
            <InlineEditableText
              as="span"
              value={benefitsText.header.eyebrow}
              editable={editMode}
              onChange={(value) => onTextChange?.('page2.benefits.header.eyebrow', value)}
            />
          )}
          headline={(
            <InlineEditableText
              as="span"
              value={benefitsText.header.headline}
              editable={editMode}
              onChange={(value) => onTextChange?.('page2.benefits.header.headline', value)}
            />
          )}
        />
      </div>
    )}
    <div
      style={{
        padding: `${space.md}px ${space.xxl}px ${space.lg + space.xs}px`,
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'auto auto',
          gap: `${space.md}px`,
          width: '100%',
          marginTop: 'auto',
        }}
      >
        {ITEMS.map((b, i) => (
          layoutEditLevel === 'detail' ? (
            <DraggableElement key={i} id={`S2-Vorteil-${i + 1}`} {...dragProps} dragAxis="y">
              <BenefitCard
                icon={b.icon}
                accentColor={b.accent}
                title={benefitsText.items[i]?.title || b.title}
                description={benefitsText.items[i]?.desc || b.desc}
              />
            </DraggableElement>
          ) : (
            <BenefitCard
              key={i}
              icon={b.icon}
              accentColor={b.accent}
              title={benefitsText.items[i]?.title || b.title}
              description={benefitsText.items[i]?.desc || b.desc}
            />
          )
        ))}
      </div>
    </div>
  </div>
  );
};
